import { Fragment, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { isPathUnder, matchesEntry } from '../utils/routeMatch';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { SidebarContext } from '../context/SidebarContext';
import { getLocalStorageItem } from '../utils/helper';
import { errorToast } from '../utils/helper';
import NavIcon from './NavIcon';

/**
 * One top-level entry in the sidebar, and its sub-menu.
 *
 * TWO WIDTHS, ONE MENU
 *
 * The sidebar has a full width and a narrow rail, and this component renders
 * both from the same menu data. At full width a group is a row that expands in
 * place. On the rail there is no room for a label, so the group becomes its
 * icon alone and its sub-menu opens as a panel alongside it — the only place
 * the labels can go once the column is 76px wide.
 *
 * Whether the rail is on is decided by the sidebar and passed in, not worked
 * out here: every entry needs the same answer, and a resize listener per entry
 * would be dozens of them watching the same number. A phone never sees it —
 * there the sidebar is a drawer that is either over the page or off it, and a
 * rail inside a drawer would be a third state with nothing to gain.
 *
 * WHY THE PANEL IS A PORTAL
 *
 * The nav scrolls, which means it clips. A panel drawn inside it would be cut
 * off at the sidebar's edge — exactly where it needs to start. It is rendered
 * into `document.body` at coordinates taken from the row instead, so it is not
 * clipped by anything, and it opens flush against the rail rather than a few
 * pixels off it so the pointer can cross without falling through the gap.
 *
 * ICONS
 *
 * Every entry carries a `navIcon` key naming what it is about, resolved to
 * artwork in navIcons.js. Sub-menu entries have them too: on the rail they are
 * all a reader has to go on before the labels appear, and at full width they
 * make a group of twenty entries scannable instead of a wall of text.
 */

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

/** How long the panel survives the pointer leaving it, in ms. */
const FLYOUT_GRACE = 140;

const Chevron = ({ open }) => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    className={classNames(
      'w-4 h-4 flex-shrink-0 transition-transform duration-200',
      open ? 'rotate-90' : 'rotate-0'
    )}
  >
    <path
      d="M7.5 4.5L13 10l-5.5 5.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

Chevron.propTypes = { open: PropTypes.bool };

const Menu = ({ item, setActiveIndex, index, openedSubMenus, setOpenedSubMenus, rail }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setShow } = useContext(SidebarContext);
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));

  const isSubMenuOpen = openedSubMenus.includes(index);
  const [showSubMenu, setShowSubMenu] = useState(isSubMenuOpen);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  const rowRef = useRef(null);
  const closeTimer = useRef(null);
  const panelRef = useRef(null);
  const [flyout, setFlyout] = useState(null);

  const isCurrentPathInSubMenu = () => {
    if (!item.subMenu || !Array.isArray(item.subMenu)) return false;

    return item.subMenu.some((subItem) => matchesEntry(location.pathname, subItem));
  };

  useEffect(() => {
    if (item.isDropDown && isCurrentPathInSubMenu()) {
      setActiveIndex(index);

      // Auto-open only once per route change
      if (!hasAutoOpened && !openedSubMenus.includes(index)) {
        setOpenedSubMenus((prev) => [...prev, index]);
        setHasAutoOpened(true);
      }
    } else {
      setHasAutoOpened(false); // Reset when route changes
    }
  }, [location.pathname]);

  useEffect(() => {
    setShowSubMenu(openedSubMenus.includes(index));
  }, [openedSubMenus, index]);

  // A panel anchored to a row that has scrolled away, or to a rail that is no
  // longer there, would float over the page on its own. Close it instead.
  useEffect(() => {
    if (!flyout) return undefined;
    const close = () => setFlyout(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [flyout]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // A panel opened by tapping never gets a pointerleave, so it would stay open
  // until something else was tapped inside it. Close it on any press that
  // lands outside both the row and the panel.
  useEffect(() => {
    if (!flyout) return undefined;
    const onDown = (event) => {
      if (rowRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setFlyout(null);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [flyout]);

  useEffect(() => {
    if (!rail) setFlyout(null);
  }, [rail]);

  const openFlyout = useCallback(() => {
    clearTimeout(closeTimer.current);
    const rect = rowRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Flush against the rail, and nudged up if the panel would run off the
    // bottom of the window. A long group opened from the last row would
    // otherwise have most of itself below the fold.
    const estimatedHeight = 52 + (item.subMenu?.filter((entry) => entry.show)?.length || 0) * 38;
    const top = Math.max(12, Math.min(rect.top, window.innerHeight - estimatedHeight - 12));

    setFlyout({ top, left: rect.right });
  }, [item.subMenu]);

  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setFlyout(null), FLYOUT_GRACE);
  }, []);

  const handleRedirect = (link) => {
    if (!link) return;
    if (item?.isDisabled) {
      return errorToast('Access denied..');
    }
    if (window.innerWidth < 1024) {
      setShow(false);
    }
    if (!item.remove) {
      navigate(link);
    } else {
      navigate(`/${userData?.slug}/permission-denied`);
    }
  };

  const toggleSubMenu = () => {
    setActiveIndex(index);
    if (openedSubMenus.includes(index)) {
      setOpenedSubMenus((prev) => prev.filter((i) => i !== index));
    } else {
      setOpenedSubMenus((prev) => [...prev, index]);
    }
  };

  const isActiveGroup =
    isPathUnder(location?.pathname, item.href) ||
    item?.subMenu?.some((entry) => matchesEntry(location?.pathname, entry));

  const handleRowClick = () => {
    // On the rail a group has nowhere to expand to, so clicking it opens the
    // panel rather than a sub-menu that would have no room for its labels.
    if (rail && item.isDropDown) {
      if (flyout) {
        setFlyout(null);
      } else {
        openFlyout();
      }
      setActiveIndex(index);
      return;
    }

    if (item.dropDownHref === 'isDropDown') {
      toggleSubMenu();
      handleRedirect(item.href);
    } else {
      handleRedirect(item.href);
      setActiveIndex(index);
    }
  };

  const subEntries = item.subMenu?.filter((entry) => entry.show) || [];

  const renderSubRow = (innerItem, subIndex, inFlyout) => (
    <div
      key={subIndex}
      onClick={() => {
        handleRedirect(innerItem.href);
        if (inFlyout) setFlyout(null);
      }}
      className={classNames(
        matchesEntry(location?.pathname, innerItem)
          ? 'text-shoorah-lightBlack dark:text-white font-medium'
          : 'text-shoorah-gray4 dark:text-[#8A8A8A] hover:text-shoorah-lightBlack dark:hover:text-white',
        'group/sub cursor-pointer outline-none flex items-center gap-3 rounded-md text-sm font-normal transition-colors',
        // Indented so a sub-icon sits clear of its parent's rather than in line
        // with it, which would read as another top-level entry. The panel needs
        // no indent: it is a list of one group and has nothing to sit under.
        inFlyout
          ? 'px-3 py-2 hover:bg-[#F9F9F9] dark:hover:bg-white/5'
          : 'lg:mx-3 mx-1 ps-6 pe-3 py-[7px] hover:bg-[#F9F9F9] dark:hover:bg-white/5'
      )}
    >
      <NavIcon name={innerItem.navIcon} className="h-[18px] w-[18px]" />
      <span className="truncate">{innerItem.name}</span>
    </div>
  );

  return (
    <>
      <div
        ref={rowRef}
        key={item.name}
        aria-label={rail ? item.name : undefined}
        onClick={handleRowClick}
        {...(rail
          ? {
              // Gated on pointer type: a tap fires pointerenter *and* click,
              // so an ungated hover handler would open the panel and the
              // click would immediately toggle it shut again.
              onPointerEnter: (event) => {
                if (event.pointerType === 'mouse') openFlyout();
              },
              onPointerLeave: (event) => {
                if (event.pointerType === 'mouse') scheduleClose();
              }
            }
          : {})}
        className={classNames(
          'cursor-pointer',
          isActiveGroup || (isSubMenuOpen && !rail)
            ? 'text-shoorah-lightBlack dark:text-white bg-[#F9F9F9] dark:bg-white/[0.06] border-y-[1.5px] border-y-[#EAEBF0] dark:border-y-transparent'
            : 'text-shoorah-lightBlack dark:text-[#D4D4D4] hover:text-black dark:hover:text-white lg:border-none lg:rounded-md border-b border-shoorah-borderGray dark:border-shoorah-darkBorder',
          'group outline-none flex items-center h-[50px] lg:h-[60px] text-sm relative font-medium',
          // `rail` already accounts for the viewport, so these need no
          // breakpoint of their own — a tablet is on the rail at 768px, where
          // an `lg:` prefix would not have fired.
          rail ? 'justify-center px-0' : 'justify-between ps-[22px] pe-4 lg:ps-6 lg:pe-4'
        )}
      >
        <div className="flex items-center min-w-0">
          <NavIcon
            name={item.navIcon}
            className={classNames('h-[22px] w-[22px]', rail ? 'mx-0' : 'me-5 ms-2')}
          />
          {!rail && <span className="truncate">{item.name}</span>}
        </div>

        {item.isDropDown && !rail && (
          <span className="ms-2 text-shoorah-gray3">
            <Chevron open={showSubMenu} />
          </span>
        )}
      </div>

      {/* Full width: the sub-menu expands in place, under its group. */}
      {item.isDropDown && showSubMenu && !rail && (
        <Transition
          show={showSubMenu}
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <div className="py-2 space-y-1 px-2">
            {item.subMenu?.map((innerItem, subIndex) => {
              return !innerItem.show ? (
                ''
              ) : innerItem.subMenu ? (
                <div className="sub-menu-menu" key={subIndex}>
                  <Menu
                    item={innerItem}
                    setActiveIndex={setActiveIndex}
                    index={`${index}-${subIndex}`} // Creating unique index for nested items
                    openedSubMenus={openedSubMenus}
                    setOpenedSubMenus={setOpenedSubMenus}
                    rail={rail}
                  />
                </div>
              ) : (
                renderSubRow(innerItem, subIndex, false)
              );
            })}
          </div>
        </Transition>
      )}

      {/* Rail: the labels move into a panel beside the sidebar. */}
      {rail &&
        flyout &&
        createPortal(
          <div
            ref={panelRef}
            onPointerEnter={(event) => {
              if (event.pointerType === 'mouse') clearTimeout(closeTimer.current);
            }}
            onPointerLeave={(event) => {
              if (event.pointerType === 'mouse') scheduleClose();
            }}
            style={{ top: flyout.top, left: flyout.left }}
            className="fixed z-[60] ms-1 w-[236px] max-h-[70vh] overflow-y-auto hide-scrollbar rounded-input border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder bg-white dark:bg-shoorah-darkBgTabColor shadow-lg py-2"
          >
            <p
              onClick={() => {
                handleRedirect(item.href);
                setFlyout(null);
              }}
              className={classNames(
                'px-3 pb-2 mb-1 border-b border-shoorah-borderGray dark:border-shoorah-darkBorder text-sm font-medium text-shoorah-lightBlack dark:text-white',
                item.href ? 'cursor-pointer' : ''
              )}
            >
              {item.name}
            </p>
            <div className="px-1 space-y-0.5">
              {subEntries.map((innerItem, subIndex) => renderSubRow(innerItem, subIndex, true))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

Menu.propTypes = {
  item: PropTypes.any,
  setActiveIndex: PropTypes.func.isRequired,
  // Nested entries are keyed "parent-child", so this is not always a number.
  index: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  openedSubMenus: PropTypes.array.isRequired,
  setOpenedSubMenus: PropTypes.func.isRequired,
  /** Icon-only mode: tablet always, desktop when the sidebar is collapsed. */
  rail: PropTypes.bool
};

export default Menu;
